// Registry delle azioni invocabili da un flow.
//
// È il cuore della sicurezza del sistema: il JSON di un flow NON contiene
// codice, contiene solo il NOME di un'azione registrata qui più i suoi
// parametri. Un admin compone azioni, non scrive JavaScript. Stessa forma
// (`type` + `config`) di `TriggerActionExecutor.php` su api-dev.
//
// Per aggiungere capacità di dominio si registrano nuove azioni da codice TS
// (vedi `src/components/commessa/wizards/commessaFlowRegistry.ts`): quella è
// l'unica via d'uscita prevista quando il dichiarativo non basta.

import type { FlowActionSpec, FlowCondition, FlowDefinition, FlowNode, FlowOption, FlowScope } from './flowTypes'
import { evaluateCondition, collectConditionIssues } from './conditions'
import { resolveDeep } from './template'
import { setScopeValue } from './scope'
import { normalizeVarName, setNodeVar } from './vars'

export interface FlowActionContext {
  /** L'oggetto su cui lavora il wizard (es. `praticaCaricata`). */
  item: unknown
  answers: Record<string, unknown>
  /** Scope completo, per condizioni e template. */
  scope: FlowScope
  /** Funzioni messe a disposizione dalla pagina host (`ctx.helpers`). */
  helpers: Record<string, unknown>
  flow?: FlowDefinition
  node?: FlowNode
}

/** Descrizione di un parametro: serve al builder per generare il form. */
export interface FlowActionParam {
  name: string
  type: 'string' | 'number' | 'boolean' | 'any' | 'object' | 'array' | 'path' | 'select'
  label?: string
  description?: string
  required?: boolean
  /** Solo per `type: 'select'`. */
  options?: FlowOption[]
  default?: unknown
  /**
   * `false` per i parametri che NON sono template: il builder smette di
   * proporre `{{...}}` e di spiegare le keyword temporali. Serve ai parametri
   * che sono un NOME (di variabile, di risposta): lì un `{{now}}` suggerito è
   * un suggerimento sbagliato.
   */
  template?: boolean
}

export interface FlowActionDefinition {
  type: string
  label: string
  description?: string
  /** Azione con effetti esterni (API, invio mail, ...). Il builder la evidenzia. */
  danger?: boolean
  params?: FlowActionParam[]
  run: (ctx: FlowActionContext, config: Record<string, unknown>) => void | Promise<void>
}

export class FlowActionError extends Error {
  constructor(message: string, readonly actionType: string, readonly cause?: unknown) {
    super(message)
    this.name = 'FlowActionError'
  }
}

// ─── Registry ─────────────────────────────────────────────────────────────────

const registry = new Map<string, FlowActionDefinition>()

export function registerFlowAction(definition: FlowActionDefinition): void {
  if (registry.has(definition.type)) {
    // Ri-registrazione: normale con l'HMR di Vite, quindi non è un warning.
    console.debug('[flow/actions] azione ridefinita:', definition.type)
  }
  registry.set(definition.type, definition)
}

export function registerFlowActions(definitions: FlowActionDefinition[]): void {
  definitions.forEach(registerFlowAction)
}

export function getFlowAction(type: string): FlowActionDefinition | undefined {
  return registry.get(type)
}

export function hasFlowAction(type: string): boolean {
  return registry.has(type)
}

/** Elenco per il builder: cosa può scegliere l'admin. */
export function listFlowActions(): FlowActionDefinition[] {
  return [...registry.values()].sort((a, b) => a.label.localeCompare(b.label))
}

/** Solo per test e per l'anteprima isolata del builder. */
export function clearFlowActions(): void {
  registry.clear()
}

// ─── Esecuzione ───────────────────────────────────────────────────────────────

export interface FlowActionOutcome {
  type: string
  skipped: boolean
  config: Record<string, unknown>
}

/**
 * Esegue una sequenza di azioni, in ordine, attendendo ognuna.
 *
 * Semantica di errore: FAIL FAST. Un'azione che fallisce interrompe la
 * sequenza con una `FlowActionError`; le azioni già eseguite restano
 * applicate. È accettabile perché — per convenzione, come nei wizard scritti
 * a mano oggi — `on_complete` muta solo l'oggetto LOCALE e la persistenza
 * avviene col "Salva" della pagina: un fallimento a metà lascia dati locali
 * incompleti che l'utente non ha ancora salvato.
 * Le azioni con effetti esterni vanno marcate `danger: true` e messe per ultime.
 */
export async function executeFlowActions(
  actions: FlowActionSpec[] | undefined,
  ctx: FlowActionContext,
): Promise<FlowActionOutcome[]> {
  const outcomes: FlowActionOutcome[] = []
  if (!actions || actions.length === 0) return outcomes

  for (const action of actions) {
    if (!action || typeof action.type !== 'string') {
      throw new FlowActionError('azione senza `type`', '(sconosciuto)')
    }

    if (action.when && !evaluateCondition(action.when as FlowCondition, ctx.scope)) {
      outcomes.push({ type: action.type, skipped: true, config: {} })
      continue
    }

    const definition = registry.get(action.type)
    if (!definition) {
      // Non si salta silenziosamente: un'azione mancante in `on_complete`
      // significherebbe perdere dati senza che nessuno se ne accorga.
      throw new FlowActionError(
        `azione non registrata: '${action.type}'. Il flow non è eseguibile su questa pagina.`,
        action.type,
      )
    }

    const config = resolveDeep({ ...(action.config ?? {}) }, ctx.scope)

    try {
      await definition.run(ctx, config)
    } catch (e) {
      throw new FlowActionError(
        `azione '${action.type}' fallita: ${e instanceof Error ? e.message : String(e)}`,
        action.type,
        e,
      )
    }

    outcomes.push({ type: action.type, skipped: false, config })
  }

  return outcomes
}

/**
 * Controlli da eseguire in fase di PUBBLICAZIONE: azioni inesistenti,
 * parametri obbligatori mancanti, condizioni `when` malformate.
 */
export function collectActionIssues(
  actions: FlowActionSpec[] | undefined,
  path = 'actions',
): string[] {
  if (!actions) return []
  const issues: string[] = []

  actions.forEach((action, i) => {
    const at = `${path}[${i}]`
    if (!action || typeof action.type !== 'string') {
      issues.push(`${at}: 'type' mancante`)
      return
    }
    const definition = registry.get(action.type)
    if (!definition) {
      issues.push(`${at}: azione non registrata '${action.type}'`)
      return
    }
    for (const param of definition.params ?? []) {
      if (param.required && (action.config?.[param.name] === undefined)) {
        issues.push(`${at} (${action.type}): parametro obbligatorio mancante '${param.name}'`)
      }
    }
    if (action.when) issues.push(...collectConditionIssues(action.when, `${at}.when`))
  })

  return issues
}

// ─── Azioni di base (nessuna dipendenza dal dominio) ──────────────────────────

registerFlowActions([
  {
    type: 'set_field',
    label: 'Imposta campo',
    description: "Scrive un valore su un campo dell'oggetto (solo item.* e answers.*).",
    params: [
      { name: 'path', type: 'path', label: 'Campo', required: true, description: 'es. ore_previste, item.noteAccettazione' },
      { name: 'value', type: 'any', label: 'Valore', description: 'Ammette template, es. {{answers.ore.previste}}' },
    ],
    run: (ctx, config) => {
      const path = config.path
      if (typeof path !== 'string' || path === '') {
        throw new Error("parametro 'path' mancante o non valido")
      }
      setScopeValue(ctx.scope, path, config.value)
    },
  },
  {
    type: 'set_field_if_present',
    label: 'Imposta campo (solo se valorizzato)',
    description: "Come 'Imposta campo', ma non scrive nulla se il valore è vuoto/null.",
    params: [
      { name: 'path', type: 'path', label: 'Campo', required: true },
      { name: 'value', type: 'any', label: 'Valore' },
    ],
    run: (ctx, config) => {
      const path = config.path
      if (typeof path !== 'string' || path === '') {
        throw new Error("parametro 'path' mancante o non valido")
      }
      const value = config.value
      if (value === null || value === undefined || value === '') return
      setScopeValue(ctx.scope, path, value)
    },
  },
  {
    type: 'set_answer',
    label: 'Imposta risposta',
    description: 'Scrive un valore nelle risposte del wizard (utile per precompilare uno step successivo).',
    params: [
      { name: 'name', type: 'string', label: 'Nome risposta', required: true, template: false },
      { name: 'value', type: 'any', label: 'Valore' },
    ],
    run: (ctx, config) => {
      const name = config.name
      if (typeof name !== 'string' || name === '') {
        throw new Error("parametro 'name' mancante o non valido")
      }
      ctx.answers[name] = config.value
    },
  },
  {
    type: 'set_answer_default',
    label: 'Precompila risposta (se vuota)',
    description: 'Imposta una risposta solo se non è già stata valorizzata dall\'utente.',
    params: [
      { name: 'name', type: 'string', label: 'Nome risposta', required: true, template: false },
      { name: 'value', type: 'any', label: 'Valore' },
    ],
    run: (ctx, config) => {
      const name = config.name
      if (typeof name !== 'string' || name === '') {
        throw new Error("parametro 'name' mancante o non valido")
      }
      if (ctx.answers[name] === undefined) ctx.answers[name] = config.value
    },
  },
  {
    type: 'set_var',
    label: 'Imposta variabile',
    description:
      "Crea o aggiorna una variabile del flow, leggibile ovunque come `vars.nome`. "
      + "È il modo per alimentare una variabile con un valore CALCOLATO, quando non "
      + "arriva da un campo (per quello c'è 'Salva anche in' sul campo stesso).",
    params: [
      {
        name: 'nome', type: 'string', label: 'Nome variabile', required: true, template: false,
        description: 'Solo lettere, cifre e underscore. Es. statoRicezioneId',
      },
      {
        name: 'valore', type: 'any', label: 'Valore',
        description: 'Ammette template, es. {{answers.ricezione.stato}} o {{now}}',
      },
    ],
    run: (ctx, config) => {
      const nome = normalizeVarName(config.nome as string | undefined)
      if (!nome) {
        throw new Error(
          `nome variabile non valido: '${String(config.nome ?? '')}'. `
          + 'Ammessi lettere, cifre e underscore, senza punti.',
        )
      }
      // La variabile si attacca al NODO che la scrive, non a un contenitore
      // globale: è ciò che le fa seguire il percorso: se l'utente torna
      // indietro e abbandona questo ramo, sparisce insieme al nodo.
      const nodeId = ctx.node?.id
      if (!nodeId) {
        throw new Error(
          "'Imposta variabile' funziona solo nelle azioni di un passo (\"Al click su Avanti\"). "
          + 'Al termine del flow non resta nessuno a leggerla: usa "Imposta campo".',
        )
      }
      setNodeVar(ctx.answers, nodeId, nome, config.valore)
    },
  },
  {
    type: 'log',
    label: 'Scrivi in console',
    description: 'Traccia un messaggio in console. Utile per il debug di un flow.',
    params: [
      {
        name: 'level', type: 'select', label: 'Livello', default: 'info',
        options: [
          { value: 'debug', label: 'debug' }, { value: 'info', label: 'info' },
          { value: 'warn', label: 'warn' }, { value: 'error', label: 'error' },
        ],
      },
      { name: 'message', type: 'string', label: 'Messaggio', required: true },
    ],
    run: (ctx, config) => {
      const level = String(config.level ?? 'info')
      const message = `[flow:${ctx.flow?.id ?? '?'}] ${String(config.message ?? '')}`
      if (level === 'error') console.error(message)
      else if (level === 'warn') console.warn(message)
      else if (level === 'debug') console.debug(message)
      else console.info(message)
    },
  },
  {
    type: 'noop',
    label: 'Nessuna operazione',
    description: 'Segnaposto: non fa nulla. Utile per completare un ramo in bozza.',
    run: () => {},
  },
])
